import React, { useEffect, useRef, useState } from 'react';
import './VinLookUp.scss';
import { useMediaQuery } from 'usehooks-ts';
import { Text, RichText } from '@sitecore-jss/sitecore-jss-react';

// 状态枚举
const STATUS = {
  IDLE: 'idle', // 默认态
  LOADING: 'loading', // 加载中
  HAS_UPGRADES: 'hasUpgrades', // 有待升级
  UP_TO_DATE: 'upToDate', // 无待升级
  INVALID_VIN: 'invalidVin', // VIN 无效
  ERROR: 'error', // 接口异常
};

// ============ API 调用函数 ============
const fetchVinStatus = async (vin) => {
  const urls = [
    `/api/sitecore/cars/GetUnfinishedTechnologyActivityByVin?vin=${vin}`, // 优先：跟随网站域名
    `https://cm-web-uat.cherynl.com/api/sitecore/cars/GetUnfinishedTechnologyActivityByVin?vin=${vin}`, // 备用
  ];

  let lastError = null;

  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data; // 成功则直接返回
    } catch (error) {
      console.warn(`Request failed for URL: ${url}`, error.message);
      lastError = error;
    }
  }

  // 所有地址都失败，抛出最后的错误
  throw lastError;
};

const VinLookUp = (props) => {
  const { fields } = props;
  const isMobile = useMediaQuery('(max-width: 768px)');

  // 输入框 ref
  const inputRef = useRef(null);

  // 状态管理
  const [vin, setVin] = useState('');
  const [status, setStatus] = useState(STATUS.IDLE);
  const [upgradeCount, setUpgradeCount] = useState(0);
  const [durationMinutes, setDurationMinutes] = useState(0);

  // VIN 输入处理：自动大写、过滤非法字符、长度限制
  const handleVinChange = (e) => {
    let raw = e.target.value.toUpperCase();
    // 剔除 I, O, Q（根据设计规范）
    raw = raw.replace(/[IOQ]/g, '');
    // 仅允许 A-Z 和 0-9
    raw = raw.replace(/[^A-Z0-9]/g, '');
    // 最大长度 17
    if (raw.length > 17) return;
    setVin(raw);
    // 输入时如果处于错误状态，清空错误态（让用户重新输入时边框变回正常）
    if (status === STATUS.INVALID_VIN || status === STATUS.ERROR) {
      setStatus(STATUS.IDLE);
    }
  };

  // 格式化时长显示（≥60分钟转为 X uur Y minuten）
  const formatDuration = (minutes) => {
    const mins = Math.round(minutes);
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return remainingMins > 0 ? `${hours} uur ${remainingMins} minuten` : `${hours} uur`;
    }
    return `${mins} minuten`;
  };

  // 提交查询
  const handleSubmit = async () => {
    // 前端校验：必须是 17 位
    if (vin.length !== 17) {
      setStatus(STATUS.INVALID_VIN);
      return;
    }

    setStatus(STATUS.LOADING);

    try {
      const result = await fetchVinStatus(vin);

      // 接口返回成功
      if (result.success && result.data?.payload) {
        const { vinExist, unfinishedCount, totalLaborMinutes } = result.data.payload;

        // ✅ 先判断 VIN 是否存在
        if (vinExist === false) {
          setStatus(STATUS.INVALID_VIN);
          return;
        }

        if (unfinishedCount > 0) {
          setUpgradeCount(unfinishedCount);
          setDurationMinutes(totalLaborMinutes || 0);
          setStatus(STATUS.HAS_UPGRADES);
        } else {
          setStatus(STATUS.UP_TO_DATE);
        }
        return;
      }

      // 接口返回失败，但 message 包含 "invalid" 或 code === "1" 视为 VIN 无效
      if (
        result.code === '1' ||
        result.message?.toLowerCase().includes('invalid') ||
        result.data?.message?.toLowerCase().includes('invalid')
      ) {
        setStatus(STATUS.INVALID_VIN);
      } else {
        // 其他业务错误
        setStatus(STATUS.ERROR);
      }
    } catch (error) {
      console.error('VIN lookup error:', error);
      setStatus(STATUS.ERROR);
    }
  };

  // 处理键盘事件（回车提交）
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  // 点击"查找经销商"跳转
  const handleFindDealer = () => {
    const link = fields?.findadealerLink?.value;
    if (link?.href && link.href !== '#') {
      window.open(link.href + (link.querystring || ''), '_self');
    } else {
      // 降级方案：如果 Sitecore 链接为空，跳转默认路径
      window.open('/nl/find-a-dealer', '_self');
    }
  };

  // ============ 渲染函数 ============

  // 状态1：有待升级
  const renderHasUpgrades = () => {
    // 从 Sitecore 获取原始 HTML
    const rawHtml = fields?.newMsg?.value || '';

    // 替换数字占位
    const replacedHtml = rawHtml
      // 替换升级数量（匹配 <strong>2</strong> 或 <strong>2</strong> 带空格的情况）
      .replace(/<strong>2<\/strong>/, `<strong>${upgradeCount}</strong>`)
      // 替换时长（匹配 45 minutes 或 45 minuten）
      .replace(/<strong>45<\/strong>/i, `<strong>${durationMinutes}</strong>`);

    return (
      <div className="result-card result-card--upgrade">
        <div className="result-card__icon">
          {/* 可替换为 SVG 图标 */}
          <span className="icon-upgrade">
            <svg
              viewBox="0 0 1024 1024"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
            >
              <path
                d="M512 0 C205.5 0 0 205.5 0 512 s205.5 512 512 512 512-205.5 512-512S818.5 0 512 0 z M562.9 738.2 c-10.6 10.4-22.8 16.7-36.3 19.1 -4.3 0.2-8.5 0.3-13 0.3s-8.7-0.1-13-0.3c-13.7-2.4-25.8-8.8-36.3-19.1 -13.8-13.6-20.7-29.8-20.7-49.1 0-19.1 6.8-35.6 20.7-49.4 13.8-13.8 30.3-20.8 49.4-20.8 19.1 0 35.6 7 49.4 20.8 13.8 13.8 20.7 30.3 20.7 49.4 0.1 19.2-6.7 35.7-20.7 49.3 z M547.1 577.4 h-65.8 c-8-97.5-37.6-231.5-37.6-249.8 0-20.9 6.5-38.5 19.6-52.6 13.2-14.3 29.9-21.5 50.4-21.5 20.4 0 37.2 7.1 50.4 21.1 13.1 14.2 19.6 31.7 19.6 52.9 0 18.3-27.3 152.4-36.6 249.9z"
                fill="#FFF3E0"
              />
            </svg>
          </span>
        </div>
        <div className="result-card__content">
          <h3 className="result-card__title">
            <div dangerouslySetInnerHTML={{ __html: replacedHtml }} />
          </h3>
        </div>
      </div>
    );
  };

  // 状态2：无待升级
  const renderUpToDate = () => (
    <div className="result-card result-card--success">
      <div className="result-card__icon">
        <span className="icon-success">
          <svg
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            data-spm-anchor-sid="a313x.search_index.0.i30.27a43a8178x7jk"
            width="48"
            height="48"
          >
            <path
              d="M874 150C674.1-50 349.9-50 150 150s-200 524.1 0 724 524.1 200 724 0 200-524.1 0-724z m-73.8 187.6L405.5 732.3s-0.1 0-0.1 0.1c0 0 0 0.1-0.1 0.1l-1 1c-12.2 12.2-32.1 12.2-44.3 0L223.3 596.7c-12.2-12.2-12.2-32.1 0-44.3l1-1c12.2-12.2 32.1-12.2 44.3 0l112.3 112.3L753.6 291c12.8-12.8 33.7-12.8 46.5 0 12.9 12.8 12.9 33.8 0.1 46.6z"
              data-spm-anchor-id="a313x.search_index.0.i31.27a43a8178x7jk"
              fill="#E6F4EA"
            ></path>
          </svg>
        </span>
      </div>
      <div className="result-card__content">
        <RichText field={fields?.successMsg} />
      </div>
    </div>
  );

  // 状态3：VIN 无效 / 接口异常
  const renderError = (isTimeout = false) => (
    <div className="result-card result-card--error">
      <div className="result-card__icon">
        <span className="icon-error">
          <svg
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            data-spm-anchor-id="a313x.search_index.0.i36.27a43a8178x7jk"
            width="48"
            height="48"
          >
            <path
              d="M511.744128 0.511744C229.261369 0.511744 0 229.261369 0 512.255872s229.261369 511.744128 511.744128 511.744128 511.744128-229.261369 511.744128-511.744128S794.226887 0.511744 511.744128 0.511744z m181.157421 647.356322c12.793603 12.793603 12.793603 32.751624 0 45.033483s-32.751624 12.793603-45.033483 0L511.744128 557.289355l-136.635682 136.635682c-12.793603 12.793603-32.751624 12.793603-45.545228 0s-12.793603-32.751624 0-45.545227L466.198901 511.744128 330.586707 376.643678c-12.793603-12.793603-12.793603-32.751624 0-45.033483 12.793603-12.793603 32.751624-12.793603 45.033483 0L511.744128 466.710645l137.65917-137.659171c12.793603-12.793603 32.751624-12.793603 45.545228 0s12.793603 32.751624 0 45.545228l-137.659171 137.65917 135.612194 135.612194z"
              data-spm-anchor-id="a313x.search_index.0.i37.27a43a8178x7jk"
              fill="#FDECEA"
            ></path>
          </svg>
        </span>
      </div>
      <div className="result-card__content">
        <RichText field={fields?.errorMsg} />
        {isTimeout && <p className="result-card__retry">{fields?.errortry?.value}</p>}
      </div>
    </div>
  );

  // 加载态
  const renderLoading = () => (
    <div className="result-card result-card--loading">
      <div className="loader">⏳</div>
      <p>{fields?.loading?.value}</p>
    </div>
  );

  // 渲染结果区
  const renderResult = () => {
    switch (status) {
      case STATUS.LOADING:
        return renderLoading();
      case STATUS.HAS_UPGRADES:
        return renderHasUpgrades();
      case STATUS.UP_TO_DATE:
        return renderUpToDate();
      case STATUS.INVALID_VIN:
        return renderError(false);
      case STATUS.ERROR:
        return renderError(true);
      default:
        return null;
    }
  };

  // 判断按钮是否可点击（17位有效VIN）
  const isSubmitDisabled = vin.length !== 17 || status === STATUS.LOADING;

  return (
    <div className={`container vin-lookup ${!isMobile ? 'pc' : 'mobile'}`}>
      <div className="fff-container">
        <div className="flex-container-text">
          {/* 标题区 */}
          <div className="vin-lookup__header">
            <RichText className="vin-lookup__title" field={fields?.checkRichText} />
          </div>

          {/* 提示 & VIN 位置说明 */}
          <div className="vin-lookup__tips">
            <RichText className="vin-lookup__tip" field={fields?.tipRichText} />
          </div>
        </div>

        {/* 输入区 */}
        <div className="vin-lookup__input-area">
          <div className="vin-lookup__label">
            <Text field={fields?.vintext} />
          </div>
          <div className="vin-lookup__input-group">
            <input
              ref={inputRef}
              type="text"
              className={`vin-lookup__input ${
                status === STATUS.INVALID_VIN || status === STATUS.ERROR ? 'is-error' : ''
              }`}
              placeholder={fields?.vinholder?.value || 'Voer uw 17-tekens VIN in'}
              value={vin}
              onChange={handleVinChange}
              onKeyDown={handleKeyDown}
              maxLength={17}
              autoFocus
              disabled={status === STATUS.LOADING}
            />
            <button
              className={`vin-lookup__btn ${isSubmitDisabled ? 'is-disabled' : ''}`}
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              <Text field={fields?.buttonText} />
            </button>
          </div>

          {/* 底部注意（含隐私政策） */}
          <div className="vin-lookup__attention">
            <RichText field={fields?.attentionTip} />
          </div>
        </div>

        {/* 结果区 - 替换显示 */}
        <div className="vin-lookup__result">{renderResult()}</div>

        {/* 底部经销商引流 */}
        <div className="vin-lookup__footer">
          <div className="vin-lookup__dealer-text">
            <RichText field={fields?.dealerText} />
          </div>
          <button className="vin-lookup__delaer-btn" onClick={handleFindDealer}>
            <Text field={fields?.findadealer} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VinLookUp;
